/*
 * Fills the sold out alternatives panel on a product page.
 *
 * Candidates are fetched after render because neither the storefront search
 * results nor Shopify's automatic recommendations exist at render time. The
 * block builds a search for the same size, category and material, limited to
 * in-stock products; if that returns nothing usable it falls back to automatic
 * recommendations.
 *
 * Size is enforced here rather than trusted to search relevance, so a shopper
 * looking at a super king is never shown a double. Rows whose size does not
 * match the product being viewed are dropped, and if that leaves nothing the
 * panel stays hidden.
 */
if (!customElements.get('sold-out-alternatives-recommendations')) {
  customElements.define(
    'sold-out-alternatives-recommendations',
    class SoldOutAlternativesRecommendations extends HTMLElement {
      observer = undefined;

      connectedCallback() {
        if (this.loaded) return;

        if (!('IntersectionObserver' in window)) {
          this.loadAlternatives();
          return;
        }

        /* This element starts hidden, and a display:none element has no box at
           all, so an observer watching it would never report it as
           intersecting and the fetch would never fire. Watch the nearest
           ancestor that is actually laid out instead. */
        const target = this.laidOutTarget();
        if (!target) {
          this.loadAlternatives();
          return;
        }

        this.observer?.disconnect();
        this.observer = new IntersectionObserver(
          (entries, observer) => {
            if (!entries.some((entry) => entry.isIntersecting)) return;
            observer.disconnect();
            this.loadAlternatives();
          },
          { rootMargin: '0px 0px 400px 0px' }
        );
        this.observer.observe(target);
      }

      disconnectedCallback() {
        this.observer?.disconnect();
      }

      laidOutTarget() {
        let node = this;

        while (node instanceof Element) {
          const rect = node.getBoundingClientRect();
          if (rect.width > 0 || rect.height > 0) return node;
          node = node.parentElement;
        }

        return null;
      }

      async fetchRows(url) {
        const response = await fetch(`${url}&section_id=${this.dataset.sectionId}`);
        if (!response.ok) return [];

        const parsed = new DOMParser().parseFromString(await response.text(), 'text/html');
        return Array.from(parsed.querySelectorAll('.sold-out-alternatives__item'));
      }

      /* Never offer a different size, and never offer the product being viewed. */
      usableRows(rows) {
        const { productId, size } = this.dataset;

        return rows.filter((row) => {
          if (row.dataset.productId === productId) return false;
          if (size && row.dataset.size !== size) return false;
          return true;
        });
      }

      /* Closest substitute first: the same finish, then the nearest price to
         the product being viewed. Nearest rather than cheapest, so someone
         looking at a $900 bundle is not led with a $200 headboard. Array sort
         is stable, so Shopify's relevance order breaks any remaining ties. */
      rankRows(rows) {
        const finish = this.dataset.finish;
        const viewedPrice = parseInt(this.dataset.price, 10);
        const hasPrice = !Number.isNaN(viewedPrice);

        if (!finish && !hasPrice) return rows;

        const matchesFinish = (row) => (finish && row.dataset.finish === finish ? 0 : 1);
        const priceGap = (row) => {
          if (!hasPrice) return 0;
          const price = parseInt(row.dataset.price, 10);
          return Number.isNaN(price) ? Number.MAX_SAFE_INTEGER : Math.abs(price - viewedPrice);
        };

        return rows.sort((a, b) => matchesFinish(a) - matchesFinish(b) || priceGap(a) - priceGap(b));
      }

      async loadAlternatives() {
        this.loaded = true;

        const { url, fallbackUrl, sectionId } = this.dataset;
        if (!url || !sectionId) return;

        try {
          let rows = this.usableRows(await this.fetchRows(url));

          if (!rows.length && fallbackUrl) {
            rows = this.usableRows(await this.fetchRows(`${fallbackUrl}&product_id=${this.dataset.productId}`));
          }

          if (!rows.length) return;

          rows = this.rankRows(rows);

          const list = this.querySelector('.sold-out-alternatives__list');
          if (!list) return;

          const limit = parseInt(this.dataset.limit, 10) || 3;

          // Still hidden at this point, so none of this is visible.
          rows.slice(0, limit).forEach((row) => list.appendChild(document.importNode(row, true)));

          this.removeAttribute('hidden');
          requestAnimationFrame(() => this.classList.add('sold-out-alternatives--revealed'));
        } catch (error) {
          console.error('Sold out alternatives: unable to load alternatives', error);
        }
      }
    }
  );
}
